import { IKeytipProps } from '@fluentui/react/lib/Keytip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';
import { getBoundingWindowWithPaddingFabric } from '@ms/window-util';

const tabOffset = 24; // Tab header keytips should be positioned 24px from the top
const peripheralButtonOffset = 24; // Keytip offset for peripheral buttons, which have smaller height than regular buttons
const tellMeControlOffset = 21; // Tell me input box has different offset due to its parent element border
const tabInMenuOffset = 5; // When in the overflow well, keytip should be positioned 5px from the right of the menu
const ribbonOffset = 32; // Ribbon item keytips should be positioned 32px from the top
const itemInMenuOffset = { x: 19, y: 17 }; // When in a menu, keytip should be positioned 19px from the left and 17px from the top
const tabbedNavigationButtonOffset = { x: 29, y: 7 }; // When in a TabbedPanelMenu, keytip should be positioned 29px from the left and 7px from the top
const coauthorGalleryPersonaOffset = 30; // Personas in coauthor gallery should be positioned 30px from the top
const coauthorGalleryOverflowOffset = 28; // Coauthor gallery overflow button should be positioned 28px from the top to be even with personas

const getDefaultKeytipProps = (
  keytipProps: IKeytipProps,
  offset: number,
  onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void,
  directionalHint: DirectionalHint = DirectionalHint.topCenter
) => {
  return {
    onReturn: defaultOnReturn,
    ...keytipProps,
    onExecute,
    calloutProps: {
      coverTarget: true,
      directionalHint,
      gapSpace: offset,
      ...keytipProps.calloutProps,
      bounds: getBoundingWindowWithPaddingFabric,
    },
  };
};

const defaultOnReturn = (executeTarget: HTMLElement) => executeTarget && executeTarget.focus();

export const deriveTabKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, tabOffset, onExecute) : undefined;
  }
);

export const deriveTellMeControlOffset = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, tellMeControlOffset, onExecute) : undefined;
  }
);

export const deriveTabMenuKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps
      ? getDefaultKeytipProps(keytipProps, tabInMenuOffset, onExecute, DirectionalHint.rightCenter)
      : undefined;
  }
);

export const deriveButtonKeytip = memoizeFunction(
  (
    keytipProps?: IKeytipProps,
    onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void
  ): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, ribbonOffset, onExecute) : undefined;
  }
);

export const derivePeripheralButtonKeytip = memoizeFunction(
  (
    keytipProps?: IKeytipProps,
    onExecute?: (executeTarget: HTMLElement, target: HTMLElement) => void
  ): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, peripheralButtonOffset, onExecute) : undefined;
  }
);

export const deriveInputKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, 26, onExecute) : undefined;
  }
);

export const deriveCheckboxKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, 22, onExecute) : undefined;
  }
);

export const derivePreviewFlyoutKeytip = memoizeFunction((keytipProps?: IKeytipProps): IKeytipProps | undefined => {
  return keytipProps ? getDefaultKeytipProps(keytipProps, 29) : undefined;
});

export const deriveMenuKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    if (keytipProps) {
      return {
        offset: itemInMenuOffset,
        ...keytipProps,
        onExecute,
        onReturn: defaultOnReturn,
        calloutProps: {
          gapSpace: 0,
          ...keytipProps.calloutProps,
          bounds: getBoundingWindowWithPaddingFabric,
        },
      };
    }
    return undefined;
  }
);

export const deriveTabbedPanelMenuKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    if (keytipProps) {
      return {
        offset: tabbedNavigationButtonOffset,
        ...keytipProps,
        onExecute,
        onReturn: defaultOnReturn,
        calloutProps: {
          gapSpace: 0,
          ...keytipProps.calloutProps,
          bounds: getBoundingWindowWithPaddingFabric,
        },
      };
    }
    return undefined;
  }
);

export const deriveCoauthorGalleryPersonaKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, coauthorGalleryPersonaOffset, onExecute) : undefined;
  }
);

export const deriveCoauthorGalleryOverflowKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    return keytipProps ? getDefaultKeytipProps(keytipProps, coauthorGalleryOverflowOffset, onExecute) : undefined;
  }
);

export const deriveMLRTopRowKeytipProps = memoizeFunction((keytipProps?: IKeytipProps, leftAlign?: boolean):
  | IKeytipProps
  | undefined => {
  if (keytipProps) {
    return {
      ...keytipProps,
      offset: leftAlign ? { x: 8, y: -17 } : undefined,
      calloutProps: {
        ...keytipProps.calloutProps,
        gapSpace: 0,
        coverTarget: false,
        directionalHint: leftAlign ? DirectionalHint.topLeftEdge : DirectionalHint.topCenter,
        bounds: getBoundingWindowWithPaddingFabric,
      },
    };
  }
  return undefined;
});

export const deriveMLRMiddleRowKeytipProps = memoizeFunction((keytipProps?: IKeytipProps, leftAlign?: boolean):
  | IKeytipProps
  | undefined => {
  if (keytipProps) {
    return {
      ...keytipProps,
      offset: leftAlign ? { x: 8, y: 0 } : undefined,
      calloutProps: {
        ...keytipProps.calloutProps,
        gapSpace: 12,
        directionalHint: leftAlign ? DirectionalHint.topLeftEdge : DirectionalHint.topCenter,
        bounds: getBoundingWindowWithPaddingFabric,
      },
    };
  }
  return undefined;
});

export const deriveMLRBottomRowKeytipProps = memoizeFunction((keytipProps?: IKeytipProps, leftAlign?: boolean):
  | IKeytipProps
  | undefined => {
  if (keytipProps) {
    return {
      ...keytipProps,
      offset: leftAlign ? { x: 8, y: 24 } : undefined,
      calloutProps: {
        ...keytipProps.calloutProps,
        directionalHint: leftAlign ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomCenter,
        gapSpace: 0,
        coverTarget: false,
        bounds: getBoundingWindowWithPaddingFabric,
      },
    };
  }
  return undefined;
});

export const deriveActionHubButtonKeytip = memoizeFunction(
  (keytipProps?: IKeytipProps, onExecute?: (executeTarget: HTMLElement) => void): IKeytipProps | undefined => {
    if (keytipProps) {
      return {
        ...keytipProps,
        onExecute,
        calloutProps: {
          ...keytipProps.calloutProps,
          directionalHint: DirectionalHint.leftCenter,
          gapSpace: -6,
          coverTarget: false,
          bounds: getBoundingWindowWithPaddingFabric,
        },
      };
    }
    return undefined;
  }
);
